/**
 * Canonical Organization (homepage) + AboutPage / ContactPage JSON-LD.
 * About and Contact reference Organization via mainEntity @id only.
 * Content comes from Prismic `organization_settings` and related slices (header, footer, contact_us).
 * @see https://developers.google.com/search/docs/appearance/structured-data/organization
 */

import { sanitizeJsonLdStrings } from "~/utils/jsonld-sanitize";

/** @param {unknown} v @returns {string} */
function trimStr(v) {
  if (v == null) return "";
  return String(v).trim();
}

/** @param {...unknown} vals @returns {string} */
function firstNonEmpty(...vals) {
  for (const v of vals) {
    const s = trimStr(v);
    if (s) return s;
  }
  return "";
}

const SCHEMA_DAY_URI_BY_KEY = {
  monday: "https://schema.org/Monday",
  tuesday: "https://schema.org/Tuesday",
  wednesday: "https://schema.org/Wednesday",
  thursday: "https://schema.org/Thursday",
  friday: "https://schema.org/Friday",
  saturday: "https://schema.org/Saturday",
  sunday: "https://schema.org/Sunday",
};

/** @param {string} t @returns {string} */
function normalizeTimeLocal(t) {
  const s = trimStr(t);
  if (!s) return "";
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return s;
  const hh = String(Number(m[1])).padStart(2, "0");
  const mm = m[2];
  const ss = m[3] != null ? m[3] : "00";
  return `${hh}:${mm}:${ss}`;
}

/**
 * @param {unknown} daysField - comma-separated day names
 * @returns {string[]}
 */
function parseDaysToSchemaUris(daysField) {
  const raw = trimStr(daysField);
  if (!raw) return [];
  const parts = raw.split(",").map(p => trimStr(p).toLowerCase());
  const uris = [];
  for (const p of parts) {
    if (!p) continue;
    const uri = SCHEMA_DAY_URI_BY_KEY[p];
    if (uri) uris.push(uri);
  }
  return uris;
}

/**
 * @param {Object|null|undefined} prismicOrg
 * @returns {Object[]}
 */
function buildOpeningHoursSpecification(prismicOrg) {
  const specs = [];
  const p = prismicOrg && typeof prismicOrg === "object" ? prismicOrg : {};
  const rules = Array.isArray(p.opening_hours_rules)
    ? p.opening_hours_rules
    : [];

  for (const rule of rules) {
    if (!rule || typeof rule !== "object") continue;
    const opens = normalizeTimeLocal(rule.opens);
    const closes = normalizeTimeLocal(rule.closes);
    const dayUris = parseDaysToSchemaUris(rule.days);
    if (!opens || !closes || !dayUris.length) continue;
    specs.push({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: dayUris.length === 1 ? dayUris[0] : dayUris,
      opens,
      closes,
    });
  }

  return specs;
}

/**
 * @param {Object|null|undefined} prismicOrg
 * @returns {Object|null}
 */
function buildPostalAddress(prismicOrg) {
  const p = prismicOrg && typeof prismicOrg === "object" ? prismicOrg : {};
  const name = trimStr(p.legal_name);
  const streetAddress = trimStr(p.street_address);
  const addressLocality = trimStr(p.address_locality);
  const addressRegion = trimStr(p.address_region);
  const postalCode = trimStr(p.postal_code);
  const addressCountry = trimStr(p.address_country);

  if (
    !streetAddress &&
    !addressLocality &&
    !addressRegion &&
    !postalCode &&
    !addressCountry
  ) {
    return null;
  }

  return {
    "@type": "PostalAddress",
    ...(name && { name }),
    ...(streetAddress && { streetAddress }),
    ...(addressLocality && { addressLocality }),
    ...(addressRegion && { addressRegion }),
    ...(postalCode && { postalCode }),
    ...(addressCountry && { addressCountry }),
  };
}

/**
 * @param {Object|null|undefined} prismicOrg
 * @returns {Object|null}
 */
function buildGeoCoordinates(prismicOrg) {
  const p = prismicOrg && typeof prismicOrg === "object" ? prismicOrg : {};
  const latRaw = trimStr(p.latitude);
  const lonRaw = trimStr(p.longitude);

  if (!latRaw || !lonRaw) {
    return null;
  }

  const latitude = Number(latRaw);
  const longitude = Number(lonRaw);
  if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
    return null;
  }

  return {
    "@type": "GeoCoordinates",
    latitude,
    longitude,
  };
}

/**
 * @param {string} baseUrl
 * @returns {string}
 */
export function organizationGraphId(baseUrl) {
  const base = (baseUrl || "").replace(/\/$/, "");
  return `${base || "https://www.snooze.com.au"}/#organization`;
}

/**
 * Full Organization JSON-LD for homepage only.
 *
 * @param {Object} store - Vuex store
 * @param {Function} getText - Prismic asText helper
 * @param {string} baseUrl
 * @returns {Object|null}
 */
export function buildHomepageOrganizationSchema(store, getText, baseUrl) {
  const org = store?.state?.prismicOrganizationSettings;
  const header = store?.state?.prismicHeader;
  const footer = store?.state?.prismicFooter;
  const contact = store?.state?.prismicContactUs;

  const rawName = header && getText(header.site_title);
  const name = typeof rawName === "string" ? rawName.trim() : "";
  if (!name) {
    return null;
  }

  const url = (baseUrl || "").replace(/\/$/, "") || "https://www.snooze.com.au";
  const graphId = organizationGraphId(url);
  const logo = header?.site_logo?.url || null;

  const headerDescription =
    header && getText(header.site_description)
      ? getText(header.site_description).trim()
      : "";

  const prismicOrgDescription =
    org && typeof org === "object"
      ? trimStr(org.organization_schema_description)
      : "";

  const description =
    firstNonEmpty(prismicOrgDescription, headerDescription) || null;

  const sameAs =
    footer?.social_icons
      ?.map(s => (s.icon_link && s.icon_link.url) || null)
      .filter(Boolean) || [];

  const enquiryEmail = firstNonEmpty(
    org?.organization_email,
    contact?.enquiry_email
  );

  const telephone = firstNonEmpty(
    org?.general_telephone,
    contact?.enquiry_phone_number
  );

  const contactType =
    (org && typeof org === "object" && trimStr(org.contact_point_type)) ||
    "General Enquiry";

  const contactPoints = [];
  if (enquiryEmail || telephone) {
    contactPoints.push({
      "@type": "ContactPoint",
      contactType,
      ...(enquiryEmail && { email: enquiryEmail }),
      ...(telephone && { telephone }),
    });
  }

  const address = buildPostalAddress(org);
  const geo = buildGeoCoordinates(org);
  const openingHoursSpecification = buildOpeningHoursSpecification(org);

  const hasLocalSignals =
    Boolean(address) || Boolean(geo) || openingHoursSpecification.length > 0;

  const schema = {
    "@context": "https://schema.org",
    "@type": hasLocalSignals
      ? ["LocalBusiness", "Organization"]
      : "Organization",
    "@id": graphId,
    name,
    url,
    ...(description && { description }),
    ...(logo && { logo }),
    ...(sameAs.length && { sameAs }),
    ...(address && { address }),
    ...(geo && { geo }),
    ...(enquiryEmail && { email: enquiryEmail }),
    ...(openingHoursSpecification.length && {
      openingHoursSpecification,
    }),
    ...(contactPoints.length && { contactPoint: contactPoints }),
  };

  return schema;
}

/**
 * @returns {Array}
 */
export function getHomepageOrganizationScript(store, prismic, baseUrl) {
  const getText = richText =>
    prismic && typeof prismic.asText === "function"
      ? prismic.asText(richText) || ""
      : "";

  const schema = buildHomepageOrganizationSchema(store, getText, baseUrl);
  if (!schema) {
    return [];
  }

  return [
    {
      hid: "organization-schema-homepage",
      type: "application/ld+json",
      innerHTML: JSON.stringify(sanitizeJsonLdStrings(schema)),
    },
  ];
}

/**
 * @param {string} description - Unique About page description (e.g. meta description)
 * @param {string} baseUrl
 * @returns {Array}
 */
export function getAboutPageSchemaScript(description, baseUrl) {
  const text =
    typeof description === "string"
      ? description.trim()
      : String(description || "").trim();

  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    mainEntity: {
      "@id": organizationGraphId(baseUrl),
    },
    ...(text && { description: text }),
  };

  return [
    {
      hid: "schema-about-page",
      type: "application/ld+json",
      innerHTML: JSON.stringify(sanitizeJsonLdStrings(schema)),
    },
  ];
}

/**
 * @param {string} baseUrl
 * @param {Object|null} contact - Prismic contact_us fields (enquiry_email, enquiry_phone_number)
 * @param {Object|null} [orgSettings] - Prismic organization_settings
 * @returns {Array}
 */
export function getContactPageSchemaScript(
  baseUrl,
  contact,
  orgSettings = null
) {
  const source = contact && typeof contact === "object" ? contact : {};
  const org = orgSettings && typeof orgSettings === "object" ? orgSettings : {};

  const email = firstNonEmpty(org.organization_email, source.enquiry_email);
  const telephone = firstNonEmpty(
    org.general_telephone,
    source.enquiry_phone_number
  );

  if (!email && !telephone) {
    return [];
  }

  const contactType = trimStr(org.contact_point_type) || "General Enquiry";

  const contactPoints = [];
  if (email || telephone) {
    contactPoints.push({
      "@type": "ContactPoint",
      contactType,
      areaServed: "AU",
      availableLanguage: "en",
      ...(email && { email }),
      ...(telephone && { telephone }),
    });
  }

  const orgId = organizationGraphId(baseUrl);
  const orgUrl =
    (baseUrl || "").replace(/\/$/, "") || "https://www.snooze.com.au";
  const legalName = trimStr(org.legal_name);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ContactPage",
        mainEntity: {
          "@id": orgId,
        },
      },
      {
        "@id": orgId,
        "@type": "Organization",
        url: orgUrl,
        ...(legalName && { name: legalName }),
        contactPoint: contactPoints,
      },
    ],
  };

  return [
    {
      hid: "schema-contact-page",
      type: "application/ld+json",
      innerHTML: JSON.stringify(sanitizeJsonLdStrings(schema)),
    },
  ];
}
